import { Suspense } from "react";
import { LinearProgress } from "@mui/material";
import cx from "classnames";
import { MonacoEditor } from "src/monaco";
import { EDITOR_OPTIONS } from "../../AiToolboxSection.constants";
import { CUSTOM_LANGUAGE_ID } from "./ChatInput.constants";
import { useChatInput, useUpdateEditorHeight } from "./ChatInput.hooks";
import type { ChatInputProps } from "./ChatInput.types";
import "./ChatInput.css";

export default function ChatInput({
  initialValue,
  hasValue,
  onChange,
  onSubmit,
  onCancel,
  isLoading,
  placeholder = "Type a message…",
  ref,
}: ChatInputProps) {
  const { handleEditorMount, heightRef } = useChatInput(onSubmit, onCancel, ref);
  useUpdateEditorHeight(heightRef);

  hasValue = hasValue || (initialValue?.trim().length ?? 0) > 0;

  const editorLoading = <LinearProgress data-test-id="ai-toolbox-editor-loading" />;

  return (
    <div
      ref={heightRef}
      className={cx(
        "flex-1 relative min-w-0",
        isLoading && "opacity-50 cursor-not-allowed"
      )}
      style={{ height: "1lh" }}
      inert={isLoading || undefined}
      aria-busy={isLoading || undefined}
    >
      {!hasValue && (
        <div className="absolute inset-y-0 left-1 right-0 flex items-center overflow-hidden text-xs text-text-secondary pointer-events-none z-1">
          {placeholder}
        </div>
      )}
      <Suspense fallback={editorLoading}>
        <MonacoEditor
          className="ai-assistant-chat-input"
          height="100%"
          language={CUSTOM_LANGUAGE_ID}
          onChange={(v) => {
            onChange(v ?? "");
          }}
          defaultValue={initialValue}
          onMount={handleEditorMount}
          options={{
            ...EDITOR_OPTIONS,
            readOnly: isLoading,
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: "on",
            suggest: { showIcons: false },
            fixedOverflowWidgets: true,
          }}
          loading={editorLoading}
        />
      </Suspense>
    </div>
  );
}
